import i18nFactory from '@yandex-int/i18n';
import { ApolloQueryResult } from 'apollo-client';
import { get, pick } from 'lodash';

import { SaveInternalAdsInput } from '../../../api/banners/BannersMutationExecutor';
import { strLengthForInternal } from '../../../service/BannerEditValidation/helpers';
import { generateNewBannerId } from '../../../service/BannerHelpers/BannerHelpers';
import { splitUrlWithSchema } from '../../../service/InternalBannerHelpers/InternalBannerHelpers';
import { BannerId, SaveInternalBannersResult } from '../../../typings/banners';
import { ProtocolValue } from '../../../typings/bannersEditState';
import { AdvType } from '../../../typings/domain';
import {
    AddInternalAds,
    adsInternalRowFragmentForEdit,
    adsInternalRowFragmentForEdit_adGroup_campaign_GdInternalFreeCampaign,
    adsInternalRowFragmentForEdit_templateVars,
    DeleteAdsWithValidation,
    GdAdPrimaryStatus,
    GdInternalTemplateInfoRequestEntryInput,
    GdResourceType,
    GdResourceValueRestrictionType,
    InternalBannersQueryForCreation,
    InternalBannersQueryForCreation_client_adGroups_rowset,
    InternalBannersQueryForCreation_client_adGroups_rowset_campaign_GdInternalFreeCampaign,
    InternalBannersQueryForEdit,
    InternalTemplatePlacesQuery,
    InternalTemplatePlacesQuery_constants_internalTemplatePlaces_rowset,
    InternalTemplateResourcesQuery,
    InternalTemplateResourcesQuery_client_internalAdTemplateInfo,
    UpdateInternalAds,
    ValidationError
} from '../../../typings/graphql-grid';
import {
    InternalBannersEditData,
    InternalPlaceTemplates,
    InternalTemplateId,
    InternalTemplateResource,
    InternalTemplateResources,
    InternalTemplateVars
} from '../../../typings/internalBanners';
import {
    EditedBannerTypeInternal,
    EditedInternalBanner,
    InternalGroupBanners,
    MappedInternalBannerData
} from '../../../typings/internalBannersEditState';
import {
    getValueFromApiFormat,
    mapCampaignRowTypeToCampaignType,
    mapGroupRowTypeToGroupType
} from '../../common/common';
import { getGroupBannersList } from '../api-data-mappers';

import * as keyset from './banner-edit-internal.i18n';

const i18n = i18nFactory(keyset);

export const mapResourcesAndValuesInternal = (
    templateId: InternalTemplateId,
    templateResources: InternalTemplateResource[] | undefined,
    values: InternalTemplateVars[] | undefined = []
): MappedInternalBannerData[] => {
    if (!templateResources) {
        return [];
    }

    return templateResources.map(resource => {
        const maxLengthRestriction = resource.restrictions.find(
            restriction =>
                restriction.strict &&
                restriction.type ===
                    GdResourceValueRestrictionType.TEXT_LENGTH_MAX
        );
        const maxLength = get(maxLengthRestriction, 'length');
        const found = values.find(val => val.resourceId === resource.id);

        let value = (found && found.value) || '';

        if (resource.type === GdResourceType.URL) {
            const {
                url = '',
                schema = ProtocolValue.https
            } = splitUrlWithSchema(value);

            value = [schema, url].join('');
        }

        return {
            resourceId: resource.id,
            type: resource.type,
            templateId: resource.templateId,
            count:
                maxLength > 0
                    ? maxLength - strLengthForInternal(value)
                    : undefined,
            label: resource.label,
            choices: resource.choices,
            required: resource.required,
            value: value
        };
    });
};

export function getEditedInternalBannerTemplateVars(
    templateId: InternalTemplateId,
    templateVars: adsInternalRowFragmentForEdit_templateVars[]
): InternalTemplateVars[] {
    return templateVars.map(templateVar => {
        return {
            value: getValueFromApiFormat(templateVar.value) || '',
            templateId: templateId,
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            resourceId: String(templateVar.templateResourceId)
        };
    });
}

export const mapApiResponseToInternalBannersData = (
    result: ApolloQueryResult<InternalBannersQueryForEdit>
): InternalBannersEditData => {
    const { client } = result.data;
    const {
        info: { id: clientId },
        ads: bannersResult
    } = client;
    const { rowset: bannersRows } = bannersResult;
    const bannersCountsMap = {};
    const placesIds = new Set<string>();

    const banners = bannersRows.reduce(
        (
            acc: Record<string, EditedInternalBanner>,
            bannerRow: adsInternalRowFragmentForEdit
        ): Record<string, EditedInternalBanner> => {
            bannersCountsMap[bannerRow.adGroup.id] = bannerRow.adGroup.adsCount;

            acc[bannerRow.id] = makeEditedBannerTypeInternal(bannerRow);

            placesIds.add(acc[bannerRow.id].campaign.placeId);

            return acc;
        },
        {}
    );

    return {
        clientId,
        banners,
        placesIds: [...placesIds],
        groupBannersList: getGroupBannersList<InternalGroupBanners>(
            banners,
            bannersCountsMap
        )
    };
};

function makeEditedBannerTypeInternal(
    bannerRow: adsInternalRowFragmentForEdit
): EditedBannerTypeInternal {
    const campaign = bannerRow.adGroup
        .campaign as adsInternalRowFragmentForEdit_adGroup_campaign_GdInternalFreeCampaign;

    return {
        // TODO: https://st.yandex-team.ru/DIRECT-98219
        id: String(bannerRow.id),
        isNew: false,
        isMobile: false,
        isSaved: false,
        readOnly: false,
        isEditAllow:
            bannerRow.access.canEdit &&
            bannerRow.status.primaryStatus !== GdAdPrimaryStatus.ARCHIVED,
        canBeDeleted: bannerRow.access.canBeDeleted || false,
        markedForDeletion: false,
        group: {
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            id: String(bannerRow.adGroup.id),
            name: bannerRow.adGroup.name,
            status: bannerRow.adGroup.status.primaryStatus,
            canEdit: bannerRow.adGroup.access.canEdit,
            type: mapGroupRowTypeToGroupType(bannerRow.adGroup.type)
        },
        campaign: {
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            id: String(campaign.id),
            name: campaign.name,
            platform: campaign.flatStrategy.platform,
            type: mapCampaignRowTypeToCampaignType(campaign.type),
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            placeId: String(campaign.placeId)
        },
        status: {
            type: getValueFromApiFormat(bannerRow.status.primaryStatus),
            isPreviousVersionShown: Boolean(
                bannerRow.status.previousVersionShown
            ),
            isRejectedOnModeration: Boolean(
                bannerRow.status.rejectedOnModeration
            )
        },

        bannerType: AdvType.INTERNAL,

        description: getValueFromApiFormat(bannerRow.description) || '',
        // TODO: https://st.yandex-team.ru/DIRECT-98219
        templateId: getValueFromApiFormat(String(bannerRow.templateId)) || '',
        templateVars: getEditedInternalBannerTemplateVars(
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            String(bannerRow.templateId),
            bannerRow.templateVars
        )
    };
}

function makeNewBannerTypeInternal(
    group: InternalBannersQueryForCreation_client_adGroups_rowset,
    bannerId: BannerId
): EditedBannerTypeInternal {
    const campaign = group.campaign as InternalBannersQueryForCreation_client_adGroups_rowset_campaign_GdInternalFreeCampaign;

    return {
        id: bannerId,
        isNew: true,
        isMobile: false,
        isSaved: false,
        isEditAllow: true,
        canBeDeleted: true,
        readOnly: false,
        markedForDeletion: false,
        group: {
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            id: String(group.id),
            name: group.name,
            status: group.status.primaryStatus,
            canEdit: true,
            type: mapGroupRowTypeToGroupType(group.type)
        },
        campaign: {
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            id: String(campaign.id),
            name: campaign.name,
            platform: campaign.flatStrategy.platform,
            type: mapCampaignRowTypeToCampaignType(campaign.type),
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            placeId: String(campaign.placeId)
        },
        status: {
            type: undefined,
            isPreviousVersionShown: false,
            isRejectedOnModeration: false
        },

        bannerType: AdvType.INTERNAL,

        description: i18n('new-banner'),
        templateId: '0',
        templateVars: []
    };
}

export const mapApiResponseToInternalNewBanner = (
    result: ApolloQueryResult<InternalBannersQueryForCreation>
): InternalBannersEditData => {
    const { client } = result.data;
    const {
        info: { id: clientId },
        adGroups
    } = client;
    const group = adGroups.rowset[0];
    const campaign = group.campaign as InternalBannersQueryForCreation_client_adGroups_rowset_campaign_GdInternalFreeCampaign;
    const groupId = group.id;

    const bannerId = generateNewBannerId(campaign.id, groupId);

    const bannersCountsMap = {};
    const banners = {};
    const placesIds = new Set<string>();

    bannersCountsMap[groupId] = group.adsCount + 1; // считаем, что новое добавляемое объявление увеличивает счетчик

    banners[bannerId] = makeNewBannerTypeInternal(group, bannerId);

    placesIds.add(banners[bannerId].campaign.placeId);

    return {
        clientId,
        banners,
        placesIds: [...placesIds],
        groupBannersList: getGroupBannersList<InternalGroupBanners>(
            banners,
            bannersCountsMap
        )
    };
};

export const mapApiResponseToPlaceTemplates = (
    result: ApolloQueryResult<InternalTemplatePlacesQuery>
): InternalPlaceTemplates => {
    const placeTemplates = {};
    const { internalTemplatePlaces } = result.data.constants;
    const { rowset } = internalTemplatePlaces;

    rowset.forEach(
        (
            row: InternalTemplatePlacesQuery_constants_internalTemplatePlaces_rowset
        ) => {
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            const placeId = String(row.placeId);
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            const templateId = String(row.templateId);

            const templateIds = placeTemplates[placeId] || [];

            placeTemplates[placeId] = templateIds;

            templateIds.push(templateId);
        }
    );

    return placeTemplates;
};

export const mapApiResponseToTemplateResources = (
    result: ApolloQueryResult<InternalTemplateResourcesQuery>
): InternalTemplateResources => {
    const { internalAdTemplateInfo } = result.data.client;

    return internalAdTemplateInfo.reduce(
        (
            templateResources: InternalTemplateResources,
            row: InternalTemplateResourcesQuery_client_internalAdTemplateInfo
        ) => {
            const {
                resources,
                resourceRestrictions,
                campaignId,
                templateId
            } = row;

            return {
                ...templateResources,

                [campaignId]: {
                    ...templateResources[campaignId],

                    [templateId]: {
                        resourceRestrictions,
                        resources: resources.map(resource => ({
                            id: resource.id,
                            // @todo https://st.yandex-team.ru/DIRECT-103006
                            type: /Возр(аст)?[^\s]+?\s+огр(анич)?[^\s]+?/.test(
                                resource.label
                            )
                                ? GdResourceType.AGE
                                : resource.type,
                            templateId: templateId,
                            label: resource.label,
                            restrictions: resource.valueRestrictions,
                            choices: resource.choices,
                            required: resourceRestrictions.every(
                                restriction => {
                                    return restriction.required.includes(
                                        resource.id
                                    );
                                }
                            )
                        }))
                    }
                }
            };
        },
        {}
    );
};

export const mapInternalBannersSaveApiResponse = (
    updateAds: UpdateInternalAds,
    addAds: AddInternalAds,
    deleteAds: DeleteAdsWithValidation,
    input: SaveInternalAdsInput
): SaveInternalBannersResult => {
    const addErrors = get(addAds, 'validationResult.errors', []);
    const updateErrors = get(updateAds, 'validationResult.errors', []);
    const deleteErrors = get(deleteAds, 'validationResult.errors', []);

    return {
        added: {
            banners: (addAds && addAds.addedAds) || [],
            errors: addErrors.map((error: ValidationError) =>
                pick(error, ['code', 'params', 'path'])
            ),
            ids: Object.keys(input.add)
        },
        updated: {
            banners: (updateAds && updateAds.updatedAds) || [],
            errors: updateErrors.map((error: ValidationError) =>
                pick(error, ['code', 'params', 'path'])
            ),
            ids: Object.keys(input.update)
        },
        deleted: {
            errors: deleteErrors.map((error: ValidationError) =>
                pick(error, ['code', 'params', 'path'])
            ),
            ids: Object.keys(input.delete)
        }
    };
};

export const mapInternalBannersTemplateResourcesEntries = (
    banners: Record<string, EditedInternalBanner>,
    placeTemplates: InternalPlaceTemplates
): GdInternalTemplateInfoRequestEntryInput[] => {
    const entries = Object.keys(banners).reduce((map, key) => {
        const { placeId, id } = banners[key].campaign;

        if (!map.has(id)) {
            map.set(id, {
                campaignId: id,
                templateIds: placeTemplates[placeId] || []
            });
        }

        return map;
    }, new Map<string, GdInternalTemplateInfoRequestEntryInput>());

    return Array.from(entries.values());
};
